import { useEffect, useState } from "react";
import type { IEquipmentTypeSlim, IResponseWithLoading } from "App/Modules/Models";
import type { IEquipmentTypeRepository } from "App/Modules/Repositories";
import type { IPageService } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services";

type EquipmentTypesResponse = IResponseWithLoading<IEquipmentTypeSlim[]>;
interface UseEquipmentTypesHook {
    (equipmentTypeRepository: IEquipmentTypeRepository, pageService: IPageService): {
        equipmentTypes: EquipmentTypesResponse;
        addEquipmentType: (newEqt: IEquipmentTypeSlim) => void;
        loadingEquipmentTypes: boolean;
    };
}

export const useEquipmentTypes: UseEquipmentTypesHook = (equipmentTypeRepository, pageService) => {
    const [equipmentTypes, setEquipmentTypes] = useState<EquipmentTypesResponse>();
    const [loadingEquipmentTypes, setLoadingEquipmentTypes] = useState(true);
    useEffect(() => {
        setLoadingEquipmentTypes(true);
        const subscription = extendWithLoadingAndFail(equipmentTypeRepository.getEquipmentTypes()).subscribe(
            (response: EquipmentTypesResponse) => {
                setEquipmentTypes(response);
                if (!response.isLoading) {
                    setLoadingEquipmentTypes(false);
                }
            },
            (error) => {
                setEquipmentTypes(error);
                if (!error.isLoading) {
                    setLoadingEquipmentTypes(false);
                }
            },
        );
        return () => {
            subscription.unsubscribe();
        };
    }, [equipmentTypeRepository, pageService]);

    const addEquipmentType = (newEqt: IEquipmentTypeSlim) => {
        setEquipmentTypes((current) => ({
            ...current,
            data: [...(current.data || []), newEqt],
        }));
    };

    return { equipmentTypes, addEquipmentType, loadingEquipmentTypes };
};
